// 本文の語 (Python、React など) の前に正式ロゴを置く rehype プラグイン。
//
// 速さのための決まりは次の通り。
// - 語の表は読み込みの 1 回だけ、字の木 (trie) の正規表現 1 本に組む。中身は決まった字の並びだけで、
//   入れ子の繰り返しが無いので後戻りが爆発しない
// - 木は 2 回だけなめる (続いて読める字の並びに分ける、語を探して置く)。1 つの節 (h2 で切った範囲) で同じ語 (Mac と iOS のように別の語は別) は最初の 1 回だけ。章のポイントとまとめの囲み (directive-point) の中は、囲みごとにも数える
// - ロゴの形は本文に載せない。語の前には、そのロゴ 1 つぶんの外部 svg (sprites.ts) を指す <use> だけを置く。
//   形のデータはこのモジュールにも入らず、ロゴを使ったページだけがブラウザで取りに行く
import map from "./map.json";
import meta from "./meta.generated.json";
import { SPRITE_URLS } from "./sprites";
import {
  CONCEPT_TERMS,
  conceptBoundaryRejects,
  conceptCompoundRejects,
  conceptContextRejects,
  conceptWord,
  isConceptId,
  type PrevWord,
} from "./conceptMarks";

type ContextRule = { after?: string[]; before?: string[]; paragraphHas?: string[] };
type LogoEntry = {
  terms: string[];
  // 本文に置く手描きの印 (tech-marks.json の id)。無い語には何も置かない (公式ロゴは本文に出さない)
  mark?: string;
  // "term" なら同じ節で省くのを語ごとにする (Mac と iOS は同じ印でも別の語)。無ければロゴ単位
  seenBy?: "term";
  context?: Record<string, ContextRule>;
};

const LOGOS = (map as { logos: Record<string, LogoEntry> }).logos;
// id → 縦横比 (幅 ÷ 高さ)。ロゴの形は持たない
const META = meta as Record<string, number>;

const TERM_TO_ID = new Map<string, string>();
// 正式ロゴの id → 本文に置く印の id (concept- 付き)。HTML と CSS のように、同じ印を指す語は同じ印として数える
const MARK_OF = new Map<string, string>();
const SEEN_BY_TERM = new Set<string>();
// 紛らわしい語 (Go、SNS) の文脈。語ごとに持つので、Amazon SNS のような複合名には掛からない
const TERM_CONTEXT = new Map<string, { rule: ContextRule; paragraph?: RegExp }>();
for (const [id, logo] of Object.entries(LOGOS)) {
  if (!META[id]) continue;
  if (logo.mark) MARK_OF.set(id, `concept-${logo.mark}`);
  for (const term of logo.terms) {
    TERM_TO_ID.set(term, id);
    if (logo.seenBy === "term") SEEN_BY_TERM.add(term);
  }
  for (const [term, rule] of Object.entries(logo.context ?? {})) {
    const words = (rule.paragraphHas ?? []).map((w) => w.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"));
    TERM_CONTEXT.set(term, {
      rule,
      paragraph: words.length
        ? new RegExp(`(?:^|[^A-Za-z0-9_])(?:${words.join("|")})(?![A-Za-z0-9_])`)
        : undefined,
    });
  }
}

const escape = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

// 語の表を字の木 (trie) にしてから正規表現に組む。語が 250 を超えても、各位置で試すのは
// 先頭の字が合う枝だけになる。平たい alternation (語を | で並べただけ) より約 3 倍速かった。
// 字の木なので同じ位置では最長の語が当たる (AWS Lambda が AWS より、HTML5 が HTML より先)。
// 量指定は (?:…)? だけで、どの枝も決まった字で始まるので、後戻りは語の長さまでに収まる
type TrieNode = { next: Map<string, TrieNode>; end: boolean };
function trieSource(terms: Iterable<string>): string {
  const root: TrieNode = { next: new Map(), end: false };
  for (const t of terms) {
    let n = root;
    for (const c of t) {
      let k = n.next.get(c);
      if (!k) n.next.set(c, (k = { next: new Map(), end: false }));
      n = k;
    }
    n.end = true;
  }
  const build = (n: TrieNode): string => {
    const alts = [...n.next].map(([c, k]) => escape(c) + build(k));
    if (alts.length === 0) return "";
    const body = alts.length === 1 ? alts[0] : `(?:${alts.join("|")})`;
    return n.end ? `(?:${body})?` : body;
  };
  return build(root);
}
const PATTERN_OFFICIAL = new RegExp(trieSource(TERM_TO_ID.keys()), "g");
// 考え方の印 (conceptMarks.ts) の語も入れた木。正式ロゴの語は、印の規則 (直前の正式ロゴ) のために読むだけで置かない。
// 印を付けない入り口 (用語集の本文など) は上の PATTERN_OFFICIAL のまま
for (const [term, id] of CONCEPT_TERMS) if (!TERM_TO_ID.has(term)) TERM_TO_ID.set(term, id);
const PATTERN_WITH_CONCEPT = new RegExp(trieSource(TERM_TO_ID.keys()), "g");

// 語の内側と見なす字。前後がこれなら語の一部なので置かない (Google の Go、GitHub の Git、MacBook の Mac)
const isWordChar = (c: number) =>
  (c >= 48 && c <= 57) || (c >= 65 && c <= 90) || (c >= 97 && c <= 122) || c === 95;
// 前がこの字なら識別子やパスの途中 (@types/React、foo.Java、#Python)
const BEFORE_JOINERS = new Set([".", "/", "@", "#", "$", "-", "\\"]);
// 後ろがこの字で、さらに英数字が続くなら識別子の途中 (React.useState、Java-based)
const AFTER_JOINERS = new Set([".", "/", "-", "\\", "@"]);
// 仮名と漢字。前がこれなら、語との間に少し空きを足す。
// 「 や 、 のような約物は字の中に空きを持つので足さない
const isCjk = (c: number) =>
  (c >= 0x3040 && c <= 0x30ff) || (c >= 0x3400 && c <= 0x9fff) || (c >= 0xf900 && c <= 0xfaff);

const SKIP_TAGS = new Set([
  "code",
  "pre",
  "kbd",
  "samp",
  "a",
  "h1",
  "h2",
  "h3",
  "h4",
  "h5",
  "h6",
  "script",
  "style",
  "svg",
  // 表のセルには付けない。表の中だけ付く語と付かない語ができて揺れて見えたため
  "table",
  "math",
  "textarea",
  "button",
  "glossary-term",
  // 中に要素を置けない、または置くと HTML の解析で外に追い出され hydration が食い違う要素
  "select",
  "option",
  "optgroup",
  "datalist",
  "title",
  "template",
  "noscript",
  "iframe",
  "object",
  "canvas",
  "video",
  "audio",
  // 中身を字として出す要素。タグを挿すと、タグの列がそのまま画面に見える
  "xmp",
  "plaintext",
  "listing",
  "noembed",
  "noframes",
]);

function isOptedOut(properties: Record<string, unknown> | undefined) {
  if (!properties) return false;
  return (
    "dataLessonDiagram" in properties ||
    "data-lesson-diagram" in properties ||
    "dataNoGlossary" in properties ||
    "data-no-glossary" in properties ||
    "dataNoIlogo" in properties ||
    "data-no-ilogo" in properties
  );
}

// 字の上では続いている (強調やリンクを挟んでも 1 語に読める) インラインの要素。
// これ以外の要素の入り口と出口は語の切れ目として扱う (段落、表のます、br など)
const INLINE_TAGS = new Set([
  "strong", "em", "b", "i", "u", "s", "del", "ins", "mark", "span", "a", "code", "kbd",
  "samp", "sub", "sup", "small", "abbr", "q", "cite", "dfn", "var", "time", "data",
  "bdi", "bdo", "ruby", "rt", "rp", "glossary-term",
  // 折り返してよい位置の印。字はつながっているので語を切らない (Python<wbr>ic は Pythonic)
  "wbr",
]);
// 語は切るが、段落は切らない要素。SNS の「同じ段落に SQS があるか」は br をまたいで見る
const SAME_PARAGRAPH_TAGS = new Set(["br", "img"]);

// glossEl は、用語集が下線を付けた要素 (glossary-term) の中の字のときだけ持つ。
// 中の字は割れないので eligible は false のまま、語が要素の字と丁寧に一致するときだけ要素ごと包む
type TextItem = { node: any; eligible: boolean; para: number; start: number; end: number; glossEl?: any };
type Run = { text: string; items: TextItem[] };
// el は印で包む用語集の要素。無ければ字を割って置く
type Hit = { start: number; end: number; id: string; term: string; el?: any };
// null は置けない。el が無ければ字を割って置き、あれば用語集の要素ごと包む
type Placement = { el?: any } | null;
type Mode = "official" | "concept";
const isSummaryBox = (node: any) =>
  String(node.tagName).toLowerCase() === "directive-point" && String(node.properties?.label ?? "").trim() === "この章のまとめ";
// 「同じ節で最初の 1 回」の置き場。章のポイントとまとめの囲みの中は囲みごとの置き場を足して引く
type SeenSet = { has(key: string): boolean; add(key: string): unknown };

/**
 * 1 つの run (語の切れ目で区切った、続いて読める字の並び) の中の語を探す。
 * 強調を挟んでも 1 本の字として見るので、AWS **Lambda** は AWS Lambda に、
 * **Git**Hub は GitHub に当たる。語の字が 1 つでも code やリンクの中にあれば置かない
 * (Git`Hub` や AWS [Lambda](…) は語として扱わない)
 */
function findHits(
  run: Run,
  seen: () => SeenSet,
  itemAt: (offset: number) => TextItem,
  placeAt: (start: number, end: number) => Placement,
  paragraphHas: (item: TextItem, re: RegExp) => boolean,
  concept: boolean,
): Hit[] {
  const full = run.text;
  const PATTERN = concept ? PATTERN_WITH_CONCEPT : PATTERN_OFFICIAL;
  PATTERN.lastIndex = 0;
  let hits: Hit[] | null = null;
  let prev: PrevWord = null;
  let m: RegExpExecArray | null;
  while ((m = PATTERN.exec(full)) !== null) {
    const term = m[0];
    const s = m.index;
    const e = s + term.length;
    const reject = () => {
      // 複合名を退けたときは 1 字先からまた探し、内側の短い語 (AWS Lambda の Lambda) に機会を残す
      PATTERN.lastIndex = s + 1;
    };
    const id = TERM_TO_ID.get(term);
    const item = itemAt(s);
    const seenKey = !concept && id && MARK_OF.has(id) ? MARK_OF.get(id)! : SEEN_BY_TERM.has(term) ? `${id}:${term}` : (id ?? term);
    if (!id) {
      reject();
      continue;
    }
    if (s > 0 && (isWordChar(full.charCodeAt(s - 1)) || BEFORE_JOINERS.has(full[s - 1]))) {
      reject();
      continue;
    }
    if (e < full.length && isWordChar(full.charCodeAt(e))) {
      reject();
      continue;
    }
    if (e + 1 < full.length && AFTER_JOINERS.has(full[e]) && isWordChar(full.charCodeAt(e + 1))) {
      reject();
      continue;
    }
    let placement: Placement = {};
    if (concept) {
      // 字の切れ目で別の語の一部なら、語として読めない。直前の語にも数えない (環境変数・ファイル の「変数」が「ファイル」を止めない)
      if (isConceptId(id) && conceptBoundaryRejects(full, s, e)) {
        reject();
        continue;
      }
      // 語として読める位置。印の規則 (直前の正式ロゴ、中黒の並び) のために、置かなくても覚える
      const before = prev;
      prev = { end: e, official: !isConceptId(id) };
      // 正式ロゴの語は先の走査で置いた。ここでは覚えるだけ
      if (!isConceptId(id)) continue;
      if (seen().has(seenKey)) {
        reject();
        continue;
      }
      // 複合語の一部 (ネットワーク層、ファイル転送) には付けない。付く語と付かない語が混ざって揺れて見えるため。
      // 語としては読めているので、直前の語には数え、同じ節の後ろの単独の語に機会を残す
      if (conceptCompoundRejects(full, s, e)) {
        reject();
        continue;
      }
      // 用語集が下線を付けた語は、要素の字と一致するときだけ要素ごと包む (印が前、下線は字に付く)。
      // 一部だけ重なる語 (仮想メモリ の メモリ) は用語集の語を割らないよう置かない
      placement = placeAt(s, e);
      if (!placement || conceptContextRejects(full, s, before)) {
        reject();
        continue;
      }
    } else if (!MARK_OF.has(id)) {
      // 手描きの印の無い語 (Docker、AWS など) は何も置かない。語の内側の短い語も探さない
      PATTERN.lastIndex = e;
      continue;
    } else if (seen().has(seenKey) || !(placement = placeAt(s, e))) {
      reject();
      continue;
    }
    const context = TERM_CONTEXT.get(term);
    if (context) {
      const { rule } = context;
      let rest = full.slice(e, e + 8);
      if (rest.startsWith(" ") || rest.startsWith(" ")) rest = rest.slice(1);
      const head = full.slice(Math.max(0, s - 8), s);
      const ok =
        rule.after?.some((c) => rest.startsWith(c)) ||
        rule.before?.some((c) => head.endsWith(c)) ||
        (context.paragraph ? paragraphHas(item, context.paragraph) : false);
      if (!ok) {
        reject();
        continue;
      }
    }

    seen().add(seenKey);
    (hits ??= []).push({ start: s, end: e, id: concept ? id : MARK_OF.get(id)!, term, el: placement.el });
  }
  return hits ?? [];
}

/**
 * 技術の語 (Python、React など) の前に、手描きの印を置く。公式ロゴの絵は本文に出さない (2026-10-19 代表の決定)。
 * 印は本文にそのまま (path) 置くので、SSR とブラウザで同じ出力になる。手描きの印の無い語には何も置かない。
 * 用語集 (rehypeGlossary) より先に走らせる
 */
export function rehypeInlineLogos() {
  return markPlugin("official");
}

/**
 * 考え方の語 (CPU、メモリなど) の前に墨の印を置く。用語集 (rehypeGlossary) のあとに走らせる。
 * 用語集と同じ字を奪い合わないよう、用語集が下線を付けた語は割らない。
 * 語が下線の要素の字と一致するときは要素ごと印で包み、一部だけ重なるときは置かない
 */
export function rehypeConceptMarks(options?: ConceptMarkOptions) {
  return markPlugin("concept", options);
}

/**
 * 考え方の印の範囲を、1 つの描き手の外へ広げるための口。
 * 項目の頁はゴールの行と本文を別々に描くので、前の描き手で付いた印を seen に渡すと、この描き手ではどの節でも付けない。
 * collect を渡すと、この木で付けた印の鍵を入れて返す (次の描き手の seen にする)。
 * scope が page なら h2 の節で付け直さず、頁で 1 回にする (既定は節ごと)
 */
export type ConceptMarkOptions = { seen?: Iterable<string>; collect?: Set<string>; scope?: "page" | "section" };

function markPlugin(mode: Mode, options?: ConceptMarkOptions) {
  return (tree: any) => {
    const concept = mode === "concept";
    const PATTERN = concept ? PATTERN_WITH_CONCEPT : PATTERN_OFFICIAL;
    // 1 回目は木をなめて、続いて読める字の並び (run) に分ける。
    // 字ごとに、置いてよい場所か、どの節か、どの段落かを持つ
    const runs: { run: Run; section: number; box: number }[] = [];
    let current: Run | null = null;
    let section = 0;
    let para = 0;
    // 章のまとめの囲み (:::point[この章のまとめ] の directive-point) の番号。0 は囲みの外
    let box = 0;
    let boxCount = 0;
    const cut = () => {
      if (current && current.text) runs.push({ run: current, section, box });
      current = null;
    };
    const collect = (node: any, eligible: boolean, glossEl?: any) => {
      if (node.type === "text") {
        if (typeof node.value !== "string" || !node.value) return;
        current ??= { text: "", items: [] };
        const start = current.text.length;
        current.text += node.value;
        current.items.push({ node, eligible, para, start, end: current.text.length, glossEl });
        return;
      }
      if (node.type !== "element" && node.type !== "root") return;
      let inline = true;
      let samePara = true;
      let ok = eligible;
      if (node.type === "element") {
        const tag = String(node.tagName).toLowerCase();
        // 先に置いた正式ロゴの絵。字が無く、語を切る要素でもない
        if (tag === "svg" && node.properties && "dataIlogo" in node.properties) return;
        if (SKIP_TAGS.has(tag) || isOptedOut(node.properties)) ok = false;
        if (concept && tag === "glossary-term") glossEl = node;
        inline = INLINE_TAGS.has(tag);
        samePara = inline || SAME_PARAGRAPH_TAGS.has(tag);
        if (!inline) cut();
        if (!samePara) para++;
        // 見出しで節が変わる。h2 の中の字は置かないが、節の区切りとしては数える
        if (tag === "h2") section++;
        if (isSummaryBox(node)) box = ++boxCount;
      }
      for (const c of node.children ?? []) collect(c, ok, glossEl);
      if (node.type === "element" && isSummaryBox(node)) {
        cut();
        box = 0;
      }
      if (!inline) cut();
      if (!samePara) para++;
    };
    collect(tree, true);
    cut();

    // 段落の字と、文脈の正規表現の答えは段落ごとに 1 回だけ組む (SNS が何千回出ても段落を読み直さない)
    let paraText: Map<number, string> | null = null;
    const paraAnswer = new Map<string, boolean>();
    const paragraphHas = (item: TextItem, re: RegExp) => {
      const key = `${item.para}:${re.source}`;
      let v = paraAnswer.get(key);
      if (v === undefined) {
        if (!paraText) {
          paraText = new Map();
          // run の間 (br の前後など) には改行を挟み、SNS<br>SQS を SNSSQS とつながない
          for (const { run } of runs) {
            const p = run.items[0].para;
            const prev = paraText.get(p);
            paraText.set(p, prev === undefined ? run.text : `${prev}\n${run.text}`);
          }
        }
        v = re.test(paraText.get(item.para) ?? "");
        paraAnswer.set(key, v);
      }
      return v;
    };

    // 2 回目は run ごとに語を探す。節が変わったら置いた印を数え直す
    const cuts = new Map<any, { at: number; to: number; id: string }[]>();
    const prevCharOf = new Map<any, number>();
    let seen = new Set<string>(options?.seen ?? []);
    let seenSection = 0;
    const placed = options?.collect;
    const track = (set: SeenSet): SeenSet => (placed ? { has: (k) => set.has(k), add: (k) => (placed.add(k), set.add(k)) } : set);
    // 章のまとめは、ポイントと同じ語を言い直す囲み。節で既に付いた語でも、囲みの中の最初の 1 回には付ける。
    // 囲みで付けた語は節の側にも数えるので、囲みの外 (本文) の付き方は変わらない。
    // ポイントや「ここがポイント」のような、本文の途中に置く囲みは節の数え方のまま
    const boxSeen = new Map<number, Set<string>>();
    for (const { run, section: sec, box: boxId } of runs) {
      if (sec !== seenSection && options?.scope !== "page") {
        // 前の描き手で付いた印 (options.seen) は、どの節でも付け直さない
        seen = new Set(options?.seen ?? []);
        seenSection = sec;
      }
      let scope: SeenSet = track(seen);
      if (boxId) {
        let local = boxSeen.get(boxId);
        if (!local) boxSeen.set(boxId, (local = new Set()));
        const inBox = local;
        const inSection = seen;
        scope = track({ has: (k) => inBox.has(k), add: (k) => (inBox.add(k), inSection.add(k)) });
      }
      if (!run.items.some((it) => it.eligible || it.glossEl)) continue;
      // 大半の run には語が無い。1 回だけ当てて、無ければ次へ
      PATTERN.lastIndex = 0;
      if (!PATTERN.test(run.text)) continue;
      // 字の位置から、その字を持つテキストを二分探索で引く (語が何千あっても候補ごとに先頭から探さない)
      const indexAt = (offset: number) => {
        let lo = 0;
        let hi = run.items.length - 1;
        while (lo < hi) {
          const mid = (lo + hi) >> 1;
          if (run.items[mid].end <= offset) lo = mid + 1;
          else hi = mid;
        }
        return lo;
      };
      const itemAt = (offset: number) => run.items[indexAt(offset)];
      // 置ける所か。字がすべて置いてよい場所なら字を割って置く。
      // 用語集の要素の字なら、語がその字とぴったり一致するときだけ要素ごと包む (割らない)
      const placeAt = (start: number, end: number): Placement => {
        let el: any;
        for (let k = indexAt(start); k < run.items.length && run.items[k].start < end; k++) {
          const it = run.items[k];
          if (it.eligible) continue;
          if (it.glossEl && it.start === start && it.end === end) {
            el = it.glossEl;
            continue;
          }
          return null;
        }
        return { el };
      };
      for (const hit of findHits(run, () => scope, itemAt, placeAt, paragraphHas, concept)) {
        const it = itemAt(hit.start);
        const key = hit.el ?? it.node;
        const list = cuts.get(key) ?? [];
        // ロゴと一緒に折り返さないよう包むのは、語のうちこの字の中にある分だけ。要素ごと包むときは字を割らない
        list.push(
          hit.el
            ? { at: 0, to: 0, id: hit.id }
            : { at: hit.start - it.start, to: Math.min(hit.end, it.end) - it.start, id: hit.id },
        );
        cuts.set(key, list);
        // 前の字が仮名か漢字かは run の中で見る (強調の直前の和文も拾う)
        if (!prevCharOf.has(key)) {
          prevCharOf.set(key, it.start > 0 ? run.text.charCodeAt(it.start - 1) : -1);
        }
      }
    }
    if (cuts.size === 0) return;

    const makeWord = conceptWord;
    const splitText = (node: any) => {
      const value: string = node.value;
      const parts: any[] = [];
      let cursor = 0;
      for (const c of cuts.get(node)!) {
        if (c.at > cursor) parts.push({ type: "text", value: value.slice(cursor, c.at) });
        const prev = c.at > 0 ? value.charCodeAt(c.at - 1) : (prevCharOf.get(node) ?? -1);
        parts.push(makeWord(c.id, value.slice(c.at, c.to), prev >= 0 && isCjk(prev)));
        cursor = c.to;
      }
      if (cursor < value.length) parts.push({ type: "text", value: value.slice(cursor) });
      return parts;
    };
    const splice = (node: any) => {
      if (!Array.isArray(node.children)) return;
      let next: any[] | null = null;
      for (let i = 0; i < node.children.length; i++) {
        const c = node.children[i];
        if (cuts.has(c)) {
          next ??= node.children.slice(0, i) as any[];
          if (c.type === "text") {
            next!.push(...splitText(c));
          } else {
            // 用語集の要素ごと包む。要素の中は触らない
            const prev = prevCharOf.get(c) ?? -1;
            next!.push(conceptWord(cuts.get(c)![0].id, c, prev >= 0 && isCjk(prev)));
          }
        } else {
          splice(c);
          next?.push(c);
        }
      }
      if (next) node.children = next;
    };
    splice(tree);
  };
}

export const INLINE_LOGO_IDS = Object.keys(META);

const TERM_TO_ID_LOWER = new Map<string, string>();
for (const [term, id] of TERM_TO_ID) TERM_TO_ID_LOWER.set(term.toLowerCase(), id);

const SHOW_OFFICIAL_LOGO = false;

export type InlineLogoRef = { id: string; ratio: number; href: string; aws: boolean };

/**
 * 語の名前から正式ロゴを引く (用語の窓の見出しが、本文の語の前の印と同じロゴを出すため)。
 * Go や SNS のように文脈で当てる語は、窓には文脈が無いので引かない
 */
export function inlineLogoForTerm(...names: Array<string | undefined>): InlineLogoRef | null {
  // 公式ロゴは窓にも出さない (2026-10-19 代表の決定)。窓の題は conceptMarkForTerm が手描きの印を引く
  if (!SHOW_OFFICIAL_LOGO) return null;
  for (const name of names) {
    if (!name) continue;
    const id = TERM_TO_ID.get(name) ?? TERM_TO_ID_LOWER.get(name.toLowerCase());
    if (!id || !SPRITE_URLS[id]) continue;
    const exact = TERM_TO_ID.has(name) ? name : [...TERM_TO_ID.keys()].find((t) => t.toLowerCase() === name.toLowerCase());
    if (exact && TERM_CONTEXT.has(exact)) continue;
    return { id, ratio: META[id], href: `${SPRITE_URLS[id]}#ilogo-${id}`, aws: id.startsWith("aws-") };
  }
  return null;
}
